import { Check, Home } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { RegistrationForm } from "../components/RegistrationForm";
import { HOST, WEBINAR, COMPLIANCE, COPYRIGHT, GREEN, LOGO_URL, scrollToId } from "../lib/content";

const TRUST_BULLETS = [
  "100% free educational session — nothing sold",
  "No hard credit pull required to attend",
  "Live Q&A — ask about your exact situation",
  "Written Game Plan available, zero obligation",
];

export const FinalRegister = () => (
  <section id="register" className="relative py-28 md:py-40 px-6 md:px-12 overflow-hidden" style={{ background: "#6D28D9" }} data-testid="final-register-section">
    <img
      src="https://images.unsplash.com/photo-1667754248024-e1e03e60eb72?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
      alt="Texas skyline at night"
      className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      style={{ opacity: 0.13, maskImage: "linear-gradient(180deg, transparent 0%, black 30%, black 75%, transparent 100%)", WebkitMaskImage: "linear-gradient(180deg, transparent 0%, black 30%, black 75%, transparent 100%)" }}
    />
    <p className="absolute bottom-5 right-6 md:right-12 font-mono-c text-[10px] tracking-[0.35em] text-white/50" data-testid="register-city-tag">📍 TEXAS</p>
    <div className="absolute left-[-15%] top-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(255,255,255,0.09), transparent 70%)" }} />
    <div className="relative max-w-[1300px] mx-auto grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
      <Reveal>
        <p className="chapter-label mb-8" style={{ color: "rgba(255,255,255,0.65)" }}>[009] — FINAL CALL</p>
        <h2 className="font-display font-bold uppercase tracking-tight text-white" style={{ fontSize: "clamp(36px, 4.6vw, 64px)", lineHeight: 1.0 }} data-testid="final-h2">
          Your Next Move Starts With a Clearer <span style={{ color: "#C4B5FD" }}>Map.</span>
        </h2>
        <p className="font-mono-c text-[11px] tracking-[0.2em] text-white/60 mt-6">📅 {WEBINAR.date} · {WEBINAR.duration} · LIVE Q&A</p>
        <ul className="mt-10 space-y-4">
          {TRUST_BULLETS.map((b, i) => (
            <li key={i} className="flex items-start gap-3 text-[15px] text-white/80" data-testid={`trust-bullet-${i}`}>
              <Check size={17} className="mt-0.5 shrink-0" style={{ color: GREEN }} />
              {b}
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.12}>
        <div className="glass-panel p-6 md:p-8" style={{ background: "rgba(255,255,255,0.96)" }} data-testid="final-registration-card">
          <p className="font-mono-c text-[10px] tracking-[0.3em] mb-1.5" style={{ color: "#6D28D9" }}>RESERVE YOUR SEAT</p>
          <h3 className="font-display font-semibold text-lg uppercase tracking-tight mb-5">First-Time Buyer Clarity — Free</h3>
          <RegistrationForm idPrefix="final" />
        </div>
      </Reveal>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="relative border-t border-black/10 pt-24 pb-10 px-6 md:px-12 overflow-hidden" data-testid="footer-section">
    <div className="absolute bottom-[-2vw] left-0 deboss-wordmark pointer-events-none">FIRST-TIME BUYER CLARITY</div>
    <div className="relative max-w-[1300px] mx-auto">
      <div className="grid md:grid-cols-3 gap-12 mb-16">
        <div>
          <div className="inline-block bg-white border border-black/10 p-3 mb-6" data-testid="footer-logo">
            <img src={LOGO_URL} alt="Yes We Can Lending Corporation" className="h-16 w-auto object-contain" />
          </div>
          <p className="font-mono-c text-[10px] tracking-[0.3em] text-black/35 mb-4">THE HOST</p>
          <p className="text-sm leading-[2] text-black/55">
            {HOST.name}<br />{HOST.company}<br />NMLS# {HOST.nmls}<br />Company NMLS# {HOST.companyNmls}<br />Licensed in {HOST.licenseStates}
          </p>
        </div>
        <div>
          <p className="font-mono-c text-[10px] tracking-[0.3em] text-black/35 mb-4">CONTACT</p>
          <p className="text-sm leading-[2] text-black/55">
            Phone: <a href="tel:+17026080721" className="hover:text-black transition-colors duration-200" data-testid="footer-phone">(702) 608-0721</a><br />
            Email: <a href="mailto:velisasellshomes@gmail.com" className="hover:text-black transition-colors duration-200" data-testid="footer-email">velisasellshomes@gmail.com</a><br />
            Web: yeswecanlending.com
          </p>
        </div>
        <div>
          <p className="font-mono-c text-[10px] tracking-[0.3em] text-black/35 mb-4">LEGAL</p>
          <div className="flex flex-col gap-2 text-sm text-black/55">
            <Link to="/privacy-policy" className="hover:text-black transition-colors duration-200" data-testid="footer-link-privacy">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-black transition-colors duration-200" data-testid="footer-link-terms">Terms & Conditions</Link>
          </div>
        </div>
      </div>
      <div id="disclaimer" className="flex items-start gap-3 border-t border-black/10 pt-8 mb-8">
        <Home size={18} className="shrink-0 mt-0.5 text-black/40" />
        <p className="text-[11px] leading-relaxed text-black/35 max-w-4xl" data-testid="footer-compliance">{COMPLIANCE}</p>
      </div>
      <p className="font-mono-c text-[10px] tracking-[0.15em] text-black/20" data-testid="footer-copyright">{COPYRIGHT}</p>
    </div>
  </footer>
);

export const MobileCTA = () => (
  <div className="fixed bottom-0 left-0 right-0 z-[90] md:hidden p-3 glass-panel border-t border-black/10" style={{ background: "rgba(255,255,255,0.88)" }} data-testid="mobile-sticky-cta">
    <button onClick={() => scrollToId("register")} className="btn-accent w-full py-4 text-[13px]" data-testid="mobile-cta-button">
      ✓ RESERVE MY FREE SEAT
    </button>
  </div>
);
